import React, { useRef, useEffect, useState } from "react";
import Webcam from "react-webcam";
import Tesseract from "tesseract.js";
import { ExecuteLayoutEventMethods } from "../../../General/commonFunctions";

export const FactsOCRTextReading = (props) => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [extractedValue, setExtractedValue] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [linePosition, setLinePosition] = useState(0);
  const [lineDirection, setLineDirection] = useState(1);

  const isMobileDevice = () => /Mobi|Android/i.test(navigator.userAgent);

  const videoConstraints = {
    facingMode: isMobileDevice() ? "environment" : "user",
  };

  const enhanceImage = (scaledImgSrc) => {
    const canvas = document.createElement("canvas");
    const img = new Image();
    img.src = scaledImgSrc;

    return new Promise((resolve) => {
      img.onload = () => {
        canvas.width = img.width * 2;
        canvas.height = img.height * 2;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Convert to grayscale
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        for (let i = 0; i < pixels.length; i += 4) {
          const grayscale =
            0.3 * pixels[i] + 0.59 * pixels[i + 1] + 0.11 * pixels[i + 2];
          pixels[i] = pixels[i + 1] = pixels[i + 2] = grayscale;
        }
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/jpeg"));
      };
    });
  };

  const detectText = async (imgSrc) => {
    try {
      setIsProcessing(true);

      const enhancedImgSrc = props.enhanceImg
        ? await enhanceImage(imgSrc)
        : imgSrc;

      // Updated OCR Config
      const config = {
        psm: 3, // Fully automatic segmentation mode
        oem: 3, // LSTM + Legacy engine
        rectangles: true,
      };

      // Add whitelist if provided
      if (props.exp) {
        config.tessedit_char_whitelist = props.exp;
      }

      // Add blacklist if provided
      if (props.blacklist) {
        config.tessedit_char_blacklist = props.blacklist;
      }

      // const { data } = await Tesseract.recognize(enhancedImgSrc, "eng", config);

      // Load Tesseract and explicitly set parameters
      const worker = await Tesseract.createWorker("eng");
      await worker.loadLanguage("eng");
      await worker.initialize("eng");

      // Force the whitelist and blacklist rules
      if (props.exp) {
        await worker.setParameters({ tessedit_char_whitelist: props.exp });
      }

      if (props.blacklist) {
        await worker.setParameters({
          tessedit_char_blacklist: props.blacklist,
        });
      }

      const { data } = await worker.recognize(enhancedImgSrc);
      await worker.terminate();

      const { text: extractedText, words } = data;

      setExtractedValue(extractedText);
      drawSparkles(words);
      ExecuteLayoutEventMethods(props.whenscan, extractedText);
    } catch (error) {
      console.error("Error recognizing text:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const drawSparkles = (words) => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const webcam = webcamRef.current;

    if (!canvas || !context || !webcam || !webcam.video) return;

    const videoWidth = webcam.video.videoWidth;
    const videoHeight = webcam.video.videoHeight;

    if (canvas.width !== videoWidth || canvas.height !== videoHeight) {
      canvas.width = videoWidth;
      canvas.height = videoHeight;
    }

    const scaleX = canvas.width / videoWidth;
    const scaleY = canvas.height / videoHeight;

    context.clearRect(0, 0, canvas.width, canvas.height);

    const sparkleCount = 20;
    words.forEach(({ bbox, confidence }) => {
      if (confidence > 85) {
        const x0 = bbox.x0 * scaleX;
        const y0 = bbox.y0 * scaleY;
        const x1 = bbox.x1 * scaleX;
        const y1 = bbox.y1 * scaleY;

        for (let i = 0; i < sparkleCount; i++) {
          const randomX = x0 + Math.random() * (x1 - x0);
          const randomY = y0 + Math.random() * (y1 - y0);
          const size = Math.random() * 2 + 1;
          context.beginPath();
          context.arc(randomX, randomY, size, 0, 2 * Math.PI, false);
          context.fillStyle = `rgba(0, 255, 0, 0.8)`;
          context.fill();
        }
      }
    });
  };

  const drawScanningLine = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!canvas || !context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "rgba(0, 255, 0, 0.6)";
    context.fillRect(0, linePosition, canvas.width, 5);
  };

  useEffect(() => {
    const lineInterval = setInterval(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const nextPosition = linePosition + lineDirection * 5;
      if (nextPosition <= 0 || nextPosition >= canvas.height) {
        setLineDirection(lineDirection * -1);
      } else {
        setLinePosition(nextPosition);
      }

      drawScanningLine();
    }, 30);

    return () => clearInterval(lineInterval);
  }, [linePosition, lineDirection]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (webcamRef.current && !isProcessing) {
        const imgSrc = webcamRef.current.getScreenshot();
        if (imgSrc) {
          detectText(imgSrc);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isProcessing]);

  return (
    <div
      style={{
        ...props.styles?.container,
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
      }}
    >
      <div
        style={{
          ...props.styles?.videoContainer,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "400px",
          position: "relative",
        }}
      >
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={videoConstraints}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            zIndex: 1,
          }}
        />
        {isProcessing && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "rgba(0, 0, 0, 0.6)",
              color: "white",
              padding: "10px 20px",
              borderRadius: "10px",
              fontSize: "18px",
              fontWeight: "bold",
            }}
          >
            Scanning...
          </div>
        )}
      </div>
    </div>
  );
};
